import {
  createCoachProgramTemplateMutation,
  getProgramTemplateQuery,
  getProgramTemplatesQuery,
  deleteProgramTemplateMutaton,
  getCustomTemplatesQuery,
  uploadTemplateMutaton,
  meQuery,
} from '@witness/graphql';
import { useApolloClient, useMutation, useQuery } from '@apollo/react-hooks';
import { useHistory, useLocation } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from '../services/utils';

const useTemplate = ({ initialFilter } = {}) => {
  const location = useLocation();
  const history = useHistory();
  const [calendarLoading, setCalendarLoading] = useState(false);

  const [templateFilter, setTemplateFilter] = useState(initialFilter || {});
  const delayedName = useDebounce(templateFilter?.templateName, 500);

  const updateFilter = (key, value) => {
    if (key === 'templateLength' && value === templateFilter?.templateLength) {
      setTemplateFilter((curr) => ({ ...curr, templateLength: undefined }));
    } else {
      setTemplateFilter((curr) => ({ ...curr, [key]: value }));
    }
  };

  const {
    data: programTemplate,
    loading: loadingProgramTemplate,
    refetch: refetchProgramTemplate,
  } = useQuery(getProgramTemplateQuery, {
    variables: {
      record: {
        uid: location.pathname.split('?')[0].split('/')[2],
      },
    },
    fetchPolicy: 'no-cache',
    skip:
      location.pathname.split('?')[0].split('/')[1] !== 'template' ||
      !location.pathname.split('?')[0].split('/')[2],
  });

  const {
    data: programTemplates,
    loading: programTemplatesLoading,
    refetch: refetchProgramTemplates,
  } = useQuery(getProgramTemplatesQuery, {
    variables: {
      filter: {
        programName: delayedName,
        programLength: templateFilter?.templateLength,
        trainingType: templateFilter?.trainingType?.value,
      },
    },
    fetchPolicy: 'network-only',
  });

  const { data: customTemplates, loading: customTemplatesLoading } = useQuery(
    getCustomTemplatesQuery,
    {
      variables: {
        filter: {
          programName: delayedName,
          programLength: templateFilter?.templateLength,
          trainingType: templateFilter?.trainingType?.value,
        },
      },
      skip: templateFilter?.searchBy?.value !== 'programs',
      partialRefetch: templateFilter?.searchBy?.value === 'programs',
      fetchPolicy: 'network-only',
    },
  );

  const [createProgramTemplate, { loading: templateChangeLoading }] = useMutation(
    createCoachProgramTemplateMutation,
  );

  const createTemplate = async (values, { setErrors }) => {
    const { templateName, templateLength, trainingTypes, uid, isGlobal } = values;
    try {
      const addedTemp = await createProgramTemplate({
        variables: {
          record: {
            uid,
            trainingTypes: trainingTypes?.map((a) => a.value),
            programName: templateName,
            programLength: templateLength,
            isGlobal,
          },
        },
      });
      history.push(`/template/${addedTemp?.data?.createOrUpdateProgramTemplate?.uid}`);
    } catch (err) {
      if (err.graphQLErrors.find((x) => x.message === 'template_name_already_exists')) {
        setErrors({ templateName: 'Name is already used' });
      }
    }
  };

  const [deleteProgramTemplate] = useMutation(deleteProgramTemplateMutaton);
  const deleteTemplate = async (uid) => {
    await deleteProgramTemplate({
      variables: {
        record: {
          uid,
        },
      },
    });
    history.push(`/templates`);
  };

  const [uploadProgramTemplate] = useMutation(uploadTemplateMutaton);
  const uploadTemplate = async (programUid, val, alert, programLength) => {
    alert.info(`The Template Is Being Uploaded`);
    try {
      await uploadProgramTemplate({
        variables: {
          record: {
            coachProgramUid: programUid,
            templateUid: val?.type === 'template' ? val?.id : undefined,
            coachProgramTemplateUid: val?.type === 'program' ? val?.id : undefined,
            programLength
          },
        },
      });
      alert.success(`Program Updated Successfully!`);
    } catch (err) {
      alert.error(`Something went wrong`);
    }
  };

  const apolloClient = useApolloClient();

  const updateCalendarCache = useCallback(
    (newCalendar) => {
      apolloClient.writeQuery({
        query: getProgramTemplateQuery,
        variables: {
          record: {
            uid: programTemplate?.uid,
          },
        },
        data: {
          ...programTemplate,
          getProgramTemplate: {
            ...programTemplate.getProgramTemplate,
            calendar: newCalendar,
          },
        },
      });
    },
    [apolloClient, programTemplate],
  );

  const { data: meData } = useQuery(meQuery);

  const readOnly = useMemo(() => {
    return programTemplate?.getProgramTemplate?.coach?.uid !== meData?.getCurrentUser?.coach?.uid;
  }, [programTemplate, meData]);

  return {
    programTemplates: programTemplates?.getProgramTemplates,
    programTemplatesLoading,
    refetchProgramTemplates,
    programTemplate: programTemplate?.getProgramTemplate,
    refetchProgramTemplate,
    loadingProgramTemplate,
    createTemplate,
    deleteTemplate,
    updateCalendarCache,
    templateChangeLoading: templateChangeLoading || loadingProgramTemplate || calendarLoading,
    setCalendarLoading,
    customTemplates: customTemplates?.getCustomTemplates,
    customTemplatesLoading,
    uploadTemplate,
    updateFilter,
    templateFilter,
    readOnly,
  };
};

export default useTemplate;
